/**
 * <release-compare> — the delivery-method room diagram.
 *
 * Shows the same room treated three ways — Reset Liquid (surface application),
 * Fast Release (gas knockdown), and Slow Release (30-day sustain) — so the
 * coverage and duration difference is obvious at a glance.
 *
 * The markup (room, furniture, molecules, odor sources, devices) is rendered by
 * the Liquid section; this element only switches the active mode, drives the two
 * meters, and swaps the caption. Mode state lives in `data-mode` on the root so
 * all the show/hide is pure CSS.
 *
 * Auto-cycles until the visitor picks a mode, then pins to their choice.
 * Respects prefers-reduced-motion: no auto-cycle and no pulsing — the visitor
 * toggles between end states instead.
 */
(function () {
  var CYCLE_MS = 3600;

  function ReleaseCompare(root) {
    this.root = root;
    this.modes = [];
    this.data = {};

    var buttons = root.querySelectorAll('[data-mode-btn]');
    for (var i = 0; i < buttons.length; i++) {
      var key = buttons[i].getAttribute('data-mode-btn');
      this.modes.push(key);
    }
    if (!this.modes.length) return;

    // Per-mode figures are authored in the theme editor and serialised into a
    // single JSON blob so the JS never hardcodes copy or numbers.
    try {
      this.data = JSON.parse(root.getAttribute('data-modes') || '{}');
    } catch (e) {
      this.data = {};
    }

    this.buttons = buttons;
    this.coverageBar = root.querySelector('[data-coverage-bar]');
    this.coverageValue = root.querySelector('[data-coverage-value]');
    this.durationBar = root.querySelector('[data-duration-bar]');
    this.durationValue = root.querySelector('[data-duration-value]');
    this.odorValue = root.querySelector('[data-odor-value]');
    this.caption = root.querySelector('[data-caption]');
    this.svg = root.querySelector('[data-room]');
    this.autoBadge = root.querySelector('[data-auto-badge]');

    this.reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.auto = !this.reduced;

    var self = this;
    for (var j = 0; j < buttons.length; j++) {
      buttons[j].addEventListener('click', function (event) {
        self.pin(event.currentTarget.getAttribute('data-mode-btn'));
      });
    }

    this.set(this.modes[0]);
    if (this.auto) this.start();
    else if (this.autoBadge) this.autoBadge.hidden = true;
  }

  ReleaseCompare.prototype.start = function () {
    var self = this;
    this.timer = window.setInterval(function () {
      var next = (self.modes.indexOf(self.mode) + 1) % self.modes.length;
      self.set(self.modes[next]);
    }, CYCLE_MS);
  };

  ReleaseCompare.prototype.stop = function () {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  };

  // A click pins the chosen mode: stop cycling and hide the "auto" badge.
  ReleaseCompare.prototype.pin = function (mode) {
    this.stop();
    this.auto = false;
    if (this.autoBadge) this.autoBadge.hidden = true;
    this.set(mode);
  };

  ReleaseCompare.prototype.set = function (mode) {
    var info = this.data[mode];
    if (!info) return;
    this.mode = mode;

    this.root.setAttribute('data-mode', mode);

    for (var i = 0; i < this.buttons.length; i++) {
      var on = this.buttons[i].getAttribute('data-mode-btn') === mode;
      this.buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    // The accent colour for the active mode drives both meters and the counter.
    this.root.style.setProperty('--rc-active', info.color);

    if (this.coverageBar) this.coverageBar.style.width = info.coverage + '%';
    if (this.coverageValue) this.coverageValue.textContent = info.coverage + '%';
    if (this.durationBar) this.durationBar.style.width = info.duration + '%';
    if (this.durationValue) this.durationValue.textContent = info.duration_label;
    if (this.odorValue) this.odorValue.textContent = info.odors;
    if (this.caption) this.caption.textContent = info.caption;
    if (this.svg) this.svg.setAttribute('aria-label', info.caption);
  };

  function init() {
    var nodes = document.querySelectorAll('[data-release-compare]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].dataset.rcBound) continue;
      nodes[i].dataset.rcBound = '1';
      new ReleaseCompare(nodes[i]);
    }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Re-initialise when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
